import React, { useEffect, useState } from "react";
import { Button, Card, Form, Input, Modal, Popconfirm, Space, Switch, Table, Tag } from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import useApiCall from "../common/api-call";
import { useToast } from "../common/toast";

const LIST_ENDPOINT = "/messaging/telegram/bot/list";
const UPSERT_ENDPOINT = "/messaging/telegram/bot/upsert";
const DELETE_ENDPOINT = "/messaging/telegram/bot/delete";
const CONFIGURE_WEBHOOK_ENDPOINT = "/messaging/telegram/bot/configureWebhook";

const TelegramView = () => {
  const { apiCall } = useApiCall();
  const { successToast, errorToast } = useToast();
  const [form] = Form.useForm();

  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingBotId, setDeletingBotId] = useState(null);
  const [configuringBotId, setConfiguringBotId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBot, setEditingBot] = useState(null);
  const [revealedTokens, setRevealedTokens] = useState({});

  const fetchBots = async () => {
    setLoading(true);
    try {
      const resp = await apiCall(LIST_ENDPOINT, "GET");
      setBots(resp?.bots || []);
    } catch (error) {
      errorToast("Failed to load telegram bots");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBots();
  }, []);

  const openCreateModal = () => {
    setEditingBot(null);
    form.setFieldsValue({
      botId: "",
      botName: "",
      test: false,
      enabled: true,
    });
    setModalOpen(true);
  };

  const openEditModal = bot => {
    setEditingBot(bot);
    form.setFieldsValue({
      botId: bot.botId,
      botName: bot.botName,
      test: !!bot.test,
      enabled: !!bot.enabled,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingBot(null);
    form.resetFields();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        botId: values.botId?.trim(),
        botName: values.botName?.trim(),
        test: !!values.test,
        enabled: values.enabled,
      };
      const botToken = values.botToken?.trim();
      if (botToken) {
        payload.botToken = botToken;
      }
      const resp = await apiCall(UPSERT_ENDPOINT, "POST", payload);

      if (resp?.success) {
        successToast(editingBot ? "Bot updated" : "Bot created");
        closeModal();
        fetchBots();
      } else {
        errorToast("Failed to save bot");
      }
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      errorToast("Failed to save bot");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async botId => {
    setDeletingBotId(botId);
    try {
      const resp = await apiCall(DELETE_ENDPOINT, "POST", { botId });
      if (resp?.success) {
        successToast("Bot deleted");
        fetchBots();
      } else {
        errorToast("Failed to delete bot");
      }
    } catch (error) {
      errorToast("Failed to delete bot");
    } finally {
      setDeletingBotId(null);
    }
  };

  const handleToggle = async (bot, enabled) => {
    try {
      const resp = await apiCall(UPSERT_ENDPOINT, "POST", {
        botId: bot.botId,
        botName: bot.botName,
        test: !!bot.test,
        enabled,
      });
      if (resp?.success) {
        successToast(enabled ? "Bot enabled" : "Bot disabled");
        fetchBots();
      } else {
        errorToast("Failed to update bot status");
      }
    } catch (error) {
      errorToast("Failed to update bot status");
    }
  };

  const handleConfigureWebhook = async bot => {
    setConfiguringBotId(bot.botId);
    try {
      const resp = await apiCall(CONFIGURE_WEBHOOK_ENDPOINT, "POST", {
        botId: bot.botId,
      });
      if (resp) {
        successToast("Webhook configured");
        Modal.success({
          title: "Webhook Configured",
          content: (
            <div>
              <div style={{ marginBottom: 8 }}>
                <strong>Webhook URL:</strong> {resp.webhookUrl || "-"}
              </div>
              <div>
                <strong>Telegram Response:</strong>
                <pre
                  style={{
                    marginTop: 8,
                    padding: 8,
                    background: "#f7f7f7",
                    borderRadius: 6,
                    maxHeight: 240,
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                  }}
                >
                  {resp.telegramResponse || "-"}
                </pre>
              </div>
            </div>
          ),
          width: 720,
        });
      } else {
        errorToast("Failed to configure webhook");
      }
    } catch (error) {
      errorToast("Failed to configure webhook");
    } finally {
      setConfiguringBotId(null);
    }
  };

  const toggleTokenReveal = botId => {
    setRevealedTokens(prev => ({
      ...prev,
      [botId]: !prev[botId],
    }));
  };

  const columns = [
    {
      title: "Bot ID",
      dataIndex: "botId",
      key: "botId",
      ellipsis: true,
    },
    {
      title: "Bot Name",
      dataIndex: "botName",
      key: "botName",
      ellipsis: true,
    },
    {
      title: "Bot Token",
      dataIndex: "botToken",
      key: "botToken",
      width: 240,
      render: (botToken, record) => {
        const isRevealed = !!revealedTokens[record.botId];
        const hasToken = !!botToken;
        const tokenText = hasToken ? (isRevealed ? botToken : "****") : "-";
        return (
          <Space>
            <span>{tokenText}</span>
            {hasToken && (
              <Button
                size="small"
                type="link"
                onClick={() => toggleTokenReveal(record.botId)}
              >
                {isRevealed ? "Hide" : "View"}
              </Button>
            )}
          </Space>
        );
      },
    },
    {
      title: "Test",
      dataIndex: "test",
      key: "test",
      width: 100,
      render: test =>
        test ? (
          <Tag color="processing">Test</Tag>
        ) : (
          <Tag color="default">Prod</Tag>
        ),
    },
    {
      title: "Status",
      dataIndex: "enabled",
      key: "enabled",
      width: 140,
      render: enabled =>
        enabled ? (
          <Tag color="success">Enabled</Tag>
        ) : (
          <Tag color="default">Disabled</Tag>
        ),
    },
    {
      title: "Enabled",
      dataIndex: "enabled",
      key: "toggle",
      width: 120,
      render: (enabled, record) => (
        <Switch checked={enabled} onChange={checked => handleToggle(record, checked)} />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 320,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            type="primary"
            onClick={() => handleConfigureWebhook(record)}
            loading={configuringBotId === record.botId}
            disabled={!!record.test}
          >
            Configure Webhook
          </Button>
          <Button size="small" onClick={() => openEditModal(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete bot"
            description={`Are you sure to delete ${record.botId}?`}
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record.botId)}
          >
            <Button
              size="small"
              danger
              loading={deletingBotId === record.botId}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card style={{ width: "100%" }}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateModal}
        >
          Add Bot
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchBots}
          loading={loading}
        >
          Refresh
        </Button>
      </Space>

      <Table
        rowKey="botId"
        loading={loading}
        dataSource={bots}
        columns={columns}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
      />

      <Modal
        title={editingBot ? "Edit Telegram Bot" : "Add Telegram Bot"}
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSave}
        okText={editingBot ? "Save" : "Create"}
        confirmLoading={saving}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Bot ID"
            name="botId"
            rules={[
              { required: true, message: "Bot ID is required" },
              { whitespace: true, message: "Bot ID is required" },
            ]}
          >
            <Input disabled={!!editingBot} placeholder="Enter bot ID" />
          </Form.Item>
          <Form.Item
            label="Bot Name"
            name="botName"
            rules={[
              { required: true, message: "Bot name is required" },
              { whitespace: true, message: "Bot name is required" },
            ]}
          >
            <Input placeholder="Enter bot name" />
          </Form.Item>
          <Form.Item
            label="Bot Token"
            name="botToken"
            rules={
              editingBot
                ? []
                : [
                  { required: true, message: "Bot token is required" },
                  { whitespace: true, message: "Bot token is required" },
                ]
            }
          >
            <Input.Password
              placeholder={
                editingBot
                  ? "Leave empty to keep current token"
                  : "Enter bot token"
              }
              visibilityToggle
            />
          </Form.Item>
          <Form.Item
            label="Test Mode"
            name="test"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="Enabled"
            name="enabled"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default TelegramView;
